import { apiCall } from "@/common/api/ApiResult.model";
import { SharedState } from "@/common/state";
import { cms } from "@/common/state/cms/cms.api";
import axios from "axios";
import { LabelProvider, provider_config } from "./provider";
import {
  LabelConfigObj,
  LabelsReadyState,
  MappableLabelRef,
  MipBaseLabelOverride,
  MipLabel,
  from_google_to_mip_label,
  MipLabelConfiguration,
  MipLabelOverride,
  CmsLabelPolicy,
} from "./model";

/** 
  @docs - https://github.com/AVAILOTECH/integrations-manager/blob/master/README.md#put-orgidlabel-mappings---upsert-label-mappings
*/

export const updateCognniLabel = apiCall<LabelConfigObj, boolean>(
  "mip-update-cognni-label",
  async (labelConfig) => {
    await axios.put("extproxy/integrations/label-configs", {
      labelConfigs: [labelConfig],
    });
    return {
      success: true,
      value: true,
    };
  }
);

export const overide_all_for_label = apiCall<
  string | { labelId: string; externalLabelProvider?: string },
  boolean
>("mip-update-cognni-label", async (payload) => {
  const cognni_label_id = typeof payload === "string" ? payload : payload.labelId;
  const externalLabelProvider =
    typeof payload === "string" ? undefined : payload.externalLabelProvider;
  await axios.put("extproxy/integrations/label-configs", {
    labelConfigs: [
      {
        labelId: cognni_label_id,
        override: true,
        ...(externalLabelProvider ? { externalLabelProvider } : {}),
      },
    ],
  });
  return {
    success: true,
    value: true,
  };
});

export const overide_none_for_label = apiCall<
  string | { labelId: string; externalLabelProvider?: string },
  boolean
>("mip-update-cognni-label", async (payload) => {
  const cognni_label_id = typeof payload === "string" ? payload : payload.labelId;
  const externalLabelProvider =
    typeof payload === "string" ? undefined : payload.externalLabelProvider;
  await axios.put("extproxy/integrations/label-configs", {
    labelConfigs: [
      {
        labelId: cognni_label_id,
        override: false,
        ...(externalLabelProvider ? { externalLabelProvider } : {}),
      },
    ],
  });
  return {
    success: true,
    value: true,
  };
});

export const getCognniLabelsConfigs = apiCall<void, LabelConfigObj[]>(
  "mip-get-cognni-labels",
  async () => {
    const res = await axios.get("extproxy/integrations/label-configs");
    return {
      success: true,
      value: res.data.value,
    };
  }
);

export const updateMipLabel = apiCall<MipLabelConfiguration, boolean>(
  "mip-update-label",
  async (label) => {
    await axios.put("extproxy/integrations/label-configs", { label });
    return {
      success: true,
      value: true,
    };
  }
);

/* export const getCognniMipLabelsReadyState = apiCall<void, LabelsReadyState>(
  "mip-cognni-labels-ready-state",
  async () => {
    const res = await axios.get(
      "/mip/labels/getLabels?cid=ca9befe5-f089-498a-8e8d-8f10f0bfd404"
    );
    return {
      success: true,
      value: res.data.missing === 0 ? "all" : "some",
    };
  }
); */

export const getAllLabelOverrides = apiCall<void, MipLabelOverride[]>(
  "mip-get-all-label-overrides",
  async () => {
    async function get_overrides(
      all_items: MipLabelOverride[],
      last_cursor: string
    ): Promise<MipLabelOverride[]> {
      const res = await axios.get(
        `/extproxy/integrations/label-overrides?inEffect=true&cursor=${last_cursor}`
      );
      const { labelOverrides, cursor } = res.data.value;
      if (labelOverrides.length > 0) {
        all_items = all_items.concat(labelOverrides);
        if (cursor) {
          return get_overrides(all_items, cursor);
        } else {
          return all_items;
        }
      } else {
        return all_items;
      }
    }
    const items = await get_overrides([], "");
    return {
      success: true,
      value: items.filter((l: any) => l.override),
    };
  }
);

export const getLabelOverrides = apiCall<string, MipLabelOverride[]>(
  "mip-get-label-overrides",
  async (label_id) => {
    async function get_overrides(
      all_items: MipLabelOverride[],
      last_cursor: string
    ): Promise<MipLabelOverride[]> {
      const res = await axios.get(
        `/extproxy/integrations/label-overrides?inEffect=true&labelId=${label_id}&cursor=${last_cursor}`
      );
      const { labelOverrides, cursor } = res.data.value;
      if (labelOverrides.length > 0) {
        all_items = all_items.concat(labelOverrides);
        if (cursor) {
          return get_overrides(all_items, cursor);
        } else {
          return all_items;
        }
      } else {
        return all_items;
      }
    }
    const items = await get_overrides([], "");
    return {
      success: true,
      value: items,
    };
  }
);

export const updateOverrides = apiCall<MipBaseLabelOverride[], boolean>(
  "mip-update-label-overrides",
  async (arr) => {
    await axios.put("/extproxy/integrations/label-overrides", {
      labelOverrides: arr,
    });
    return {
      success: true,
      value: true,
    };
  }
);

// https://app.clickup.com/3752527/v/dc/3jgjf-26305/3jgjf-13925
/**
 * The provider's own label list. Microsoft serves MipLabel shape directly;
 * Google serves its own shape and is normalised by from_google_to_mip_label so
 * everything downstream of here stays provider-agnostic.
 */
export const getMipLabelsTree = apiCall<
  LabelProvider | void,
  { labels: MipLabel[]; missing: number; is_empty: boolean }
>("mip-get-labels-tree", async (provider) => {
  const cfg = provider_config(provider as LabelProvider);
  const res = await axios.get(cfg.labels_endpoint);
  // An unrouted path resolves to the SPA's index.html with a 200, so `res.data`
  // can be an HTML string rather than the expected envelope. Spreading that
  // yields character-indexed junk and `labels` is undefined, which used to
  // throw inside the caller's reduce and leave the screen stuck on "Loading
  // Labels & Mappings" instead of showing its empty state. Treat anything that
  // is not a label array as an empty result.
  const raw = res.data && res.data.labels;
  if (!Array.isArray(raw)) {
    return {
      success: true,
      value: { labels: [], missing: 0, is_empty: true },
    };
  }
  const labels =
    cfg.id === "google" ? raw.map(from_google_to_mip_label) : raw;
  return {
    success: true,
    value: {
      ...res.data,
      labels,
      is_empty: res.status === 204,
    },
  };
});
//#endregion

export const download_mip_console_app = async () => {
  try {
    const res = await fetch(
      `${SharedState.env.value.server}/mip/labels/downloadApp`,
      {
        headers: {
          authorization: SharedState.loggedUser.value.authorization,
        },
      }
    );
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "mip_console_app";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
    return {
      success: true,
    };
  } catch (e) {
    console.log("mip_console_app download error : ", e);
    return {
      success: false,
    };
  }
};

export const get_label_mapping_combinations = apiCall<
  { externalLabelProvider?: string } | void,
  Array<{
    labelId: string;
    mipLabelId: string;
    overrideLabelIds: string[];
    externalLabelProvider?: string;
  }>
>("get-mip-coggni-label-mapping", async (payload) => {
  const provider = payload && payload.externalLabelProvider;
  const res = await axios.get(
    "/extproxy/integrations/label-mappings?includeLabelingType=true" +
      (provider ? "&externalLabelProvider=" + provider : "")
  );
  return {
    success: true,
    value: res.data.value.labelMappings,
  };
});

// https://github.com/AVAILOTECH/integrations-manager/blob/master/README.md#put-orgidlabel-mappings---upsert-label-mappings
export const create_label_mapping_combinations = apiCall<
  {
    labelId: string;
    mipLabelId: string;
    mipLabelName: string;
    mipLabelFullName: string;
    externalLabelProvider?: string;
  },
  boolean
>(
  "create-mip-coggni-label-mapping",
  async ({
    labelId,
    mipLabelId,
    mipLabelName,
    mipLabelFullName,
    externalLabelProvider,
  }) => {
    // The integrations-manager payload keeps Microsoft's field names for every
    // provider: a Google label's name goes in mipLabelName/mipLabelFullName and
    // its id in mipLabelId. Legacy, deliberately kept.
    await axios.put("/extproxy/integrations/label-mappings", {
      labelMappings: [
        {
          labelId,
          mipLabelId,
          mipLabelFullName,
          mipLabelName,
          ...(externalLabelProvider ? { externalLabelProvider } : {}),
        },
      ],
    });
    return {
      success: true,
      value: true,
    };
  }
);

export const delete_label_mapping_combinations = apiCall<
  string | { labelId: string; externalLabelProvider?: string },
  boolean
>("create-mip-coggni-label-mapping", async (payload) => {
  const params = new URLSearchParams();
  if (typeof payload === "string") {
    params.append("labelId", payload);
  } else {
    params.append("labelId", payload.labelId);
    if (payload.externalLabelProvider) {
      params.append("externalLabelProvider", payload.externalLabelProvider);
    }
  }
  await axios.delete(
    `/extproxy/integrations/label-mappings?${params.toString()}`
  );
  return {
    success: true,
    value: true,
  };
});

/* ------------------------------------------------------------------ *
 * label-api's normalized beta endpoints (task 25038)
 *
 * One call per change, with the provider's settings under its own key
 * (`application`, e.g. "mip"), in place of integrations-manager's separate
 * label-mappings / label-configs / label-overrides writes. Reads are unchanged.
 * ------------------------------------------------------------------ */

const LABEL_API_BETA = "/label-api/beta";

/**
 * Map a Cognni label to an external label, with its automation settings:
 *
 *   PUT /label-api/beta/mapping
 *   { labelId, mip: { labelId, labelFullName, labelName, automate, confidenceThreshold } }
 */
export const put_label_mapping_beta = apiCall<
  {
    labelId: string;
    application: string;
    external: { labelId: string; labelFullName: string; labelName: string };
    automate: boolean;
    /** 0..1, as label-configs takes it (LabelTreshHold.normalize) */
    confidenceThreshold: number;
  },
  boolean
>("label-api-beta-mapping", async ({ labelId, application, external, automate, confidenceThreshold }) => {
  await axios.put(`${LABEL_API_BETA}/mapping`, {
    labelId,
    [application]: {
      labelId: external.labelId,
      labelFullName: external.labelFullName,
      labelName: external.labelName,
      automate,
      confidenceThreshold,
    },
  });
  return { success: true, value: true };
});

/**
 * A Cognni label's overrides, whole: override every external label (`full`),
 * or the ones in `yes` and not the ones in `no`.
 *
 *   PUT /label-api/beta/override
 *   { labelId, mip: { full, yes: [externalLabelId...], no: [externalLabelId...] } }
 */
export const put_label_override_beta = apiCall<
  { labelId: string; application: string; full: boolean; yes: string[]; no: string[] },
  boolean
>("label-api-beta-override", async ({ labelId, application, full, yes, no }) => {
  await axios.put(`${LABEL_API_BETA}/override`, {
    labelId,
    [application]: { full, yes, no },
  });
  return { success: true, value: true };
});

/**
 * Unlink a Cognni label from its external label, turning its automation and
 * overrides off in the same call:
 *
 *   DELETE /label-api/beta/mapping?labelId=..&automate=false&override=false&application=mip
 */
export const delete_label_mapping_beta = apiCall<
  { labelId: string; application: string },
  boolean
>("label-api-beta-delete-mapping", async ({ labelId, application }) => {
  const params = new URLSearchParams({
    labelId,
    automate: "false",
    override: "false",
    application,
  });
  await axios.delete(`${LABEL_API_BETA}/mapping?${params.toString()}`);
  return { success: true, value: true };
});

export const get_mappable_labels = apiCall<void, MappableLabelRef[]>(
  "get-mappable-labels",
  async () => {
    const res = await axios.get("/automated-label-mapping/mappable-labels");
    return { success: true, value: res.data.value.mappableLabels };
  }
);

export const get_label_policies = async () => {
  return await cms<void, CmsLabelPolicy[]>(
    (env) => `
  *[_type=="label_policies"]{
    ...
  }
  `
  )();
};
